import type { MachineRow as ContractRow } from "@rally/agreed";
import { Alert, Col, Tabs, Tappable, Text, useConfirm, useTheme } from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import type { Card } from "../chat/panel/stacking.ts";
import { failureSaid } from "../localization/failure.ts";
import { say } from "../localization/say.ts";
import { act, read } from "../net/rally.ts";

type Machine = ContractRow;

type Activity = { at: string; kind: string; by: string }[];

export function machineCard(machine: Machine): Card {
  return {
    key: `machine:${machine.id}`,
    title: machine.label,
    render: () => <Machine machine={machine} />,
  };
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Col style={{ gap: 2 }}>
      <Text typeName="caption" color="text.tertiary">
        {label}
      </Text>
      <Text typeName="body" color="text.primary">
        {value}
      </Text>
    </Col>
  );
}

function Machine({ machine }: { machine: Machine }) {
  const theme = useTheme();
  const [tab, setTab] = useState("overview");
  const [row, setRow] = useState<Machine>(machine);
  const [activity, setActivity] = useState<Activity | null>(null);
  const [wrong, setWrong] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  const again = useCallback(() => {
    void read<Machine[]>("machine.list")
      .then((all) => {
        const said = all.find((one) => one.id === machine.id);
        if (said) setRow(said);
      })
      .catch(() => {});
  }, [machine.id]);

  useEffect(() => {
    again();
    if (tab === "activity") {
      void read<Activity>("machine.activity", { machine: machine.id })
        .then(setActivity)
        .catch(() => setActivity([]));
    }
  }, [again, tab, machine.id]);

  const revoked = row.status === "revoked";

  return (
    <Col style={{ gap: theme.space[4] }}>
      {dialog}
      {wrong ? <Alert tone="danger">{wrong}</Alert> : null}
      <Tabs
        items={[
          { id: "overview", label: say("machine.tab.overview") },
          { id: "access", label: say("machine.tab.access") },
          { id: "activity", label: say("machine.tab.activity") },
          { id: "security", label: say("machine.tab.security") },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === "overview" ? (
        <Col testID="machine-overview" style={{ gap: theme.space[3] }}>
          <Field label={say("machine.overview.label")} value={row.label} />
          <Field label={say("machine.overview.fingerprint")} value={row.fingerprint} />
          <Field
            label={say("machine.overview.status")}
            value={say(`machines.status.${row.status}` as "machines.status.active")}
          />
          <Text typeName="caption" color="text.tertiary">
            {say("machine.overview.registered")(new Date(row.createdAt).toLocaleString())}
          </Text>
          {row.pairedAt ? (
            <Text typeName="caption" color="text.tertiary">
              {say("machine.overview.paired")(new Date(row.pairedAt).toLocaleString())}
            </Text>
          ) : null}
          {row.revokedAt ? (
            <Text typeName="caption" color="text.tertiary">
              {say("machine.overview.revoked")(new Date(row.revokedAt).toLocaleString())}
            </Text>
          ) : null}
        </Col>
      ) : null}
      {tab === "access" ? (
        <Col testID="machine-access" style={{ gap: theme.space[2] }}>
          <Text typeName="caption" color="text.tertiary">
            {say("machine.access.scopes")}
          </Text>
          {row.capabilities.length === 0 ? (
            <Text typeName="body" color="text.primary">
              {say("machine.access.none")}
            </Text>
          ) : (
            row.capabilities.map((one) => (
              <Text key={one} typeName="body" color="text.primary">
                {one}
              </Text>
            ))
          )}
          <Text typeName="caption" color="text.tertiary">
            {say("machine.access.changedHere")}
          </Text>
        </Col>
      ) : null}
      {tab === "activity" ? (
        <Col testID="machine-activity" style={{ gap: theme.space[2] }}>
          <Text typeName="caption" color="text.tertiary">
            {say("machine.activity.title")}
          </Text>
          {(activity ?? []).length === 0 ? (
            <Text typeName="body" color="text.primary">
              {say("machine.activity.empty")}
            </Text>
          ) : (
            (activity ?? []).map((line) => (
              <Text key={`${line.at}-${line.kind}`} typeName="body" color="text.primary">
                {`${line.kind} · ${new Date(line.at).toLocaleString()}`}
              </Text>
            ))
          )}
        </Col>
      ) : null}
      {tab === "security" ? (
        <Col testID="machine-security" style={{ gap: theme.space[3] }}>
          <Text typeName="caption" color="text.tertiary">
            {say("machine.security.passkeyNote")}
          </Text>
          {revoked ? (
            <Text typeName="body" color="text.primary">
              {say("machine.security.revoked")}
            </Text>
          ) : (
            <Tappable
              accessibilityRole="button"
              accessibilityLabel={say("machine.security.revoke")}
              testID="machine-revoke"
              onPress={() => {
                void ask({
                  title: say("machine.security.revoke"),
                  description: say("machine.security.revokeWhy"),
                  confirmLabel: say("machine.security.revoke"),
                  tone: "danger",
                }).then((sure) => {
                  if (!sure) return;
                  setWrong(null);
                  void act("machine.revoke", { machine: machine.id })
                    .then(again)
                    .catch((cause) => setWrong(failureSaid(cause)));
                });
              }}
              style={{
                paddingVertical: theme.space[2],
                paddingHorizontal: theme.space[3],
                borderRadius: theme.radius.md,
              }}
            >
              <Text typeName="body" color="accent">
                {say("machine.security.revoke")}
              </Text>
            </Tappable>
          )}
          <Text typeName="caption" color="text.tertiary">
            {say("machine.security.revokeWhy")}
          </Text>
        </Col>
      ) : null}
    </Col>
  );
}
